import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppToast } from '@/hooks/useAppToast';
import { apiCall } from '@/util/api';
import { IMPRESSION_GOAL_MAX_DEFAULT, useAppConfig } from '@/modules/config';
import { useCasbin, ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { useUserContext } from '@/modules/user/useUserContext';
import { downsampleTopicImage } from '../image';
import { TopicLogic } from '../logic';
import { buildTopicMutations } from '../mutations';
import { useTopicForm } from '../useTopicForm';
import type { Topic } from '../types';
import type { SharingSetupDraft } from './types';
import { useTopicController } from '../useTopicController';
import { useTopicState } from '../TopicStateContext';
import type { TopicViewMode } from '../TopicView';
import {
  MAX_BODY_LENGTH,
  acceptedTopicImageTypes,
  getErrorCode,
  imageModerationRejectedCode,
} from './constants';

/**
 * What a create or update sends: the topic's editable fields, with each
 * setting always present, since an omitted one clears it server-side.
 */
interface TopicPayload
  extends Pick<
      Topic,
      | 'title'
      | 'body'
      | 'startTime'
      | 'endTime'
      | 'timezone'
      | 'type'
      | 'customTemplates'
      | 'byline'
      | 'location'
    >,
    Required<
      Pick<
        Topic,
        | 'introCountLimit'
        | 'onlyManagersCanShare'
        | 'byInviteOnly'
        | 'impressionGoal'
        | 'reshareStorySettings'
      >
    > {
  /** Left out unless there is an image to set, or one to remove with null. */
  imageData?: string | null;
}

interface UseTopicUpsertParams {
  initialMode: TopicViewMode;
  topicId?: string;
}

/**
 * The shared create/edit controller: composes `useTopicForm` + `useTopicController`
 * and adds the topic mutation logic (POST create / PUT update, form validation,
 * image handling, the news race-goal in-flight warning, and the create permission
 * check). This is the single definition of upsert behavior, consumed by both the
 * legacy `TopicView` create/update branch and the component-driven `TopicCreateOrUpdatePage`
 * shell (which hands the bundle to each type's `EditForm`) — so the paths cannot
 * drift.
 */
export function useTopicUpsert({ initialMode, topicId }: UseTopicUpsertParams) {
  const history = useHistory();
  const { claims } = useUserContext();
  const [present] = useAppToast();
  const { features } = useAppConfig();
  const { isInitialized: casbinInitialized, permissionsMap } = useCasbin();
  const { dispatch: dispatchTopicState } = useTopicState();

  const form = useTopicForm();
  const controller = useTopicController({
    initialMode,
    topicId,
    resetCreateForm: form.resetCreateForm,
    resetFormFromTopic: form.resetFormFromTopic,
  });

  // Destructure the form fields the mutation callbacks read, so each callback
  // depends on the specific stable values (matching the legacy `TopicView` deps)
  // rather than the whole per-render form object.
  const {
    topicTitle,
    topicBody,
    topicImageData,
    topicImageDataChanged,
    setTopicImageData,
    startUTC,
    endUTC,
    endBeforeStart,
    safeTimezone,
    type,
    impressionGoal,
    introLimitCount,
    introLimitCountEnabled,
    onlyManagersCanShare,
    byInviteOnly,
    customTemplates,
    setCustomTemplates,
    reshareStorySettings,
    setReshareStorySettings,
    byline,
    location,
  } = form;
  const { topic, handleFinishUpdate } = controller;

  const [saving, setSaving] = useState(false);

  const canCreateTopics =
    casbinInitialized &&
    (permissionsMap[ACTIONS.CREATE]?.includes(RESOURCES.TOPICS) ?? false);

  const globalIntroMax =
    typeof features.introCountMax === 'number' && features.introCountMax >= 0
      ? features.introCountMax
      : null;

  const validateTopicForm = useCallback(async () => {
    if (!topicTitle.trim()) {
      await present({
        message: 'Please enter a topic title',
        duration: 2000,
        color: 'danger',
      });
      return false;
    }

    if (topicBody.length > MAX_BODY_LENGTH) {
      await present({
        message: `Body must be ${MAX_BODY_LENGTH} characters or fewer`,
        duration: 2000,
        color: 'danger',
      });
      return false;
    }

    if (endBeforeStart) {
      await present({
        message: 'End time must be after start time',
        duration: 2000,
        color: 'danger',
      });
      return false;
    }

    return true;
  }, [endBeforeStart, topicTitle, topicBody, present]);

  const handleSelectTopicImage = useCallback(
    async (selectedFile: File | null) => {
      if (!selectedFile) {
        return;
      }

      if (!acceptedTopicImageTypes.has(selectedFile.type)) {
        await present({
          message: 'Please choose a JPEG, PNG, or WebP image',
          duration: 3000,
          color: 'danger',
        });
        return;
      }

      try {
        setTopicImageData(await downsampleTopicImage(selectedFile));
      } catch {
        await present({
          message: 'Could not process image',
          duration: 3000,
          color: 'danger',
        });
      }
    },
    [present, setTopicImageData]
  );

  const presentTopicMutationError = useCallback(
    async (response: Response, fallbackMessage: string) => {
      const errorCode = await getErrorCode(response);
      if (errorCode === imageModerationRejectedCode) {
        await present({
          message: 'Image does not meet Snowball community guidelines',
          duration: 3000,
          color: 'danger',
        });
        return;
      }

      const errorText = await response.text();
      await present({
        message: errorText || fallbackMessage,
        duration: 3000,
        color: 'danger',
      });
    },
    [present]
  );

  // Any topic type may set an impression goal (#447), clamped client-side to
  // the global ceiling (the backend clamps again at write and read time). Null
  // means "no goal" — and clears any stored goal on save, matching the other
  // payload fields. Always sent, since an omitted key clears it server-side.
  //
  // The clamp is TopicLogic's, the same one the read view measures progress
  // against, so the form can't drift from what the topic will display. It
  // absorbs the NaN an empty field parses to.
  const effectiveGoal = useCallback(
    (): number | null =>
      TopicLogic.effectiveImpressionGoal(
        parseInt(impressionGoal, 10),
        features.impressionGoalMax ?? IMPRESSION_GOAL_MAX_DEFAULT
      ),
    [impressionGoal, features.impressionGoalMax]
  );

  // The form as a create or update sends it, the image aside: the one
  // definition both requests and the change check build from.
  const topicPayload = useCallback(
    (): TopicPayload => ({
      title: topicTitle,
      body: topicBody,
      startTime: startUTC.toISOString(),
      endTime: TopicLogic.hasEndTime(type) ? endUTC.toISOString() : undefined,
      timezone: safeTimezone,
      type,
      introCountLimit: introLimitCountEnabled
        ? parseInt(introLimitCount, 10) || null
        : null,
      onlyManagersCanShare,
      byInviteOnly,
      customTemplates,
      impressionGoal: effectiveGoal(),
      reshareStorySettings,
      byline: byline.trim() || undefined,
      location: location.trim() || undefined,
    }),
    [
      topicTitle,
      topicBody,
      startUTC,
      endUTC,
      type,
      safeTimezone,
      introLimitCountEnabled,
      introLimitCount,
      onlyManagersCanShare,
      byInviteOnly,
      customTemplates,
      effectiveGoal,
      reshareStorySettings,
      byline,
      location,
    ]
  );

  // Whether saving would change the loaded topic: the form as a draft topic,
  // compared field by field — the core fields the change log tracks, and every
  // other saved setting, so edits on the nested sharing setup count too.
  const hasChanges = useMemo(() => {
    if (!topic) {
      return false;
    }
    const draft: Topic = {
      ...topic,
      ...topicPayload(),
      imageData: topicImageData ?? undefined,
    };
    return (
      TopicLogic.computeCoreDiffs(topic, draft) !== null ||
      TopicLogic.computeOtherDiffs(topic, draft).length > 0
    );
  }, [topic, topicPayload, topicImageData]);

  // Everything the per-type sharing setup edits, as one prop for each EditForm
  // to hand on.
  const sharingSetup = useMemo(
    (): SharingSetupDraft => ({
      customTemplates,
      setCustomTemplates,
      reshareStorySettings,
      setReshareStorySettings,
      defaultStartingSenderAppositive:
        TopicLogic.defaultStartingSenderAppositive(type),
      hasImpressionGoal: effectiveGoal() != null,
      topicType: type,
    }),
    [
      customTemplates,
      setCustomTemplates,
      reshareStorySettings,
      setReshareStorySettings,
      type,
      effectiveGoal,
    ]
  );

  const createTopic = useCallback(async () => {
    if (!(await validateTopicForm())) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[CreateTopic] createTopic called:', {
      claims,
      canCreateTopics,
    });

    setSaving(true);

    try {
      const payload = topicPayload();
      if (topicImageData) {
        payload.imageData = topicImageData;
      }

      const response = await apiCall('/api/topics', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const newTopic = TopicLogic.fromJSON(data);
        dispatchTopicState({ type: 'UPSERT_TOPIC', topic: newTopic });

        await present({
          message: `Topic "${newTopic.title}" created successfully!`,
          duration: 3000,
          color: 'success',
        });

        setTimeout(() => {
          history.push('/topics');
        }, 1500);
      } else if (response.status === 403) {
        await present({
          message: 'You do not have permission to create topics',
          duration: 3000,
          color: 'danger',
        });
      } else if (response.status === 401) {
        await present({
          message: 'Authentication failed. Please link your account again.',
          duration: 3000,
          color: 'danger',
        });
      } else {
        await presentTopicMutationError(response, 'Failed to create topic');
      }
    } catch {
      await present({
        message: 'Network error occurred',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setSaving(false);
    }
  }, [
    canCreateTopics,
    claims,
    topicImageData,
    topicPayload,
    dispatchTopicState,
    history,
    present,
    presentTopicMutationError,
    validateTopicForm,
  ]);

  // Changing a news's race goal while a run is still working toward it could
  const updateTopic = useCallback(async () => {
    if (!topicId) {
      return;
    }

    if (!(await validateTopicForm())) {
      return;
    }

    setSaving(true);

    try {
      const encodedTopicId = encodeURIComponent(topicId);
      const payload = topicPayload();
      if (topicImageDataChanged) {
        payload.imageData = topicImageData;
      }

      const response = await apiCall(`/api/topics/${encodedTopicId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedTopic = TopicLogic.fromJSON(data);

        // Append this update's per-field mutations to the session change log so
        // the change-history subpage can show what changed over time. `topic` is
        // still the pre-update value here — handleFinishUpdate upserts the new
        // one below. All fields in one save share a single timestamp (#418).
        const at =
          updatedTopic.updatedAt &&
          !Number.isNaN(Date.parse(updatedTopic.updatedAt))
            ? updatedTopic.updatedAt
            : new Date().toISOString();
        const mutations = topic
          ? buildTopicMutations(topic, updatedTopic, at)
          : [];
        if (mutations.length) {
          dispatchTopicState({
            type: 'LOG_TOPIC_MUTATIONS',
            topicId,
            mutations,
          });
        }

        await present({
          message: `Topic "${updatedTopic.title}" updated successfully!`,
          duration: 3000,
          color: 'success',
        });

        handleFinishUpdate(updatedTopic);
      } else if (response.status === 403) {
        await present({
          message: 'You do not have permission to update this topic',
          duration: 3000,
          color: 'danger',
        });
      } else if (response.status === 401) {
        await present({
          message: 'Authentication failed. Please link your account again.',
          duration: 3000,
          color: 'danger',
        });
      } else {
        await presentTopicMutationError(response, 'Failed to update topic');
      }
    } catch {
      await present({
        message: 'Network error occurred',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setSaving(false);
    }
  }, [
    topic,
    handleFinishUpdate,
    dispatchTopicState,
    topicId,
    topicImageData,
    topicImageDataChanged,
    topicPayload,
    present,
    presentTopicMutationError,
    validateTopicForm,
  ]);

  return {
    ...form,
    ...controller,
    saving,
    canCreateTopics,
    globalIntroMax,
    handleSelectTopicImage,
    createTopic,
    updateTopic,
    effectiveGoal,
    sharingSetup,
    hasChanges,
  };
}
